import { defineStore } from 'pinia';
import http, { fetchCSRFToken } from '@/services/http';
import router from '@/router';
import eventBus, { EventTypes } from '@/services/eventBus';
import { API_PATHS } from '@/config/api';

// Globale Variable außerhalb des Stores definieren
let logoutInProgress = false;

// Hilfsfunktion zum Abrufen von Cookies
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'));
  return match ? match[3] : null;
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: localStorage.getItem('token') || null,
    user: JSON.parse(localStorage.getItem('user') || 'null'),
    impersonating: JSON.parse(localStorage.getItem('impersonating') || 'false'),
    originalUserId: localStorage.getItem('originalUserId') || null,
    company: JSON.parse(localStorage.getItem('company') || 'null')
  }),
  
  getters: {
    isAuthenticated: (state) => !!state.token && !!state.user,
    isSuperAdmin: (state) => state.user && state.user.role === 'super_admin',
    isCompanyAdmin: (state) => state.user && state.user.role === 'admin',
    isImpersonating: (state) => state.impersonating,
    getUser: (state) => state.user,
    getToken: (state) => state.token,
    getOriginalUserId: (state) => state.originalUserId,
    getCompany: (state) => state.company
  },
  
  actions: {
    setToken(token) {
      this.token = token;
      if (token) {
        localStorage.setItem('token', token);
      } else {
        localStorage.removeItem('token');
      }
    },
    
    setUser(user) {
      this.user = user;
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
      } else {
        localStorage.removeItem('user');
      }
    },
    
    setImpersonating(status) {
      this.impersonating = status;
      localStorage.setItem('impersonating', JSON.stringify(status));
    },
    
    setOriginalUserId(id) {
      this.originalUserId = id;
      localStorage.setItem('originalUserId', id);
    },
    
    setCompany(company) {
      this.company = company;
      if (company) {
        localStorage.setItem('company', JSON.stringify(company));
        localStorage.setItem('currentCompany', JSON.stringify(company));
        if (company.id) {
          localStorage.setItem('companyId', company.id.toString());
        }
      } else {
        localStorage.removeItem('company');
        localStorage.removeItem('currentCompany');
        localStorage.removeItem('companyId');
      }
    },
    
    async login(credentials) {
      try {
        // CSRF-Token vor dem Login abrufen
        await fetchCSRFToken();
        
        // Logging für Debugging-Zwecke
        console.log('Login-Anfrage mit:', credentials.email);
        
        // Login-Anfrage senden
        const response = await http.post(API_PATHS.LOGIN, credentials);
        
        // Token und Benutzer setzen
        const { token, user } = response.data;
        this.setToken(token);
        this.setUser(user);
        
        // Firmen-Cache im localStorage löschen
        localStorage.removeItem('currentCompany');
        localStorage.removeItem('companyId');
        
        // Token im localStorage speichern, wenn "remember" aktiviert ist
        if (credentials.remember) {
          localStorage.setItem('token', token);
        }
        
        return response.data;
      } catch (error) {
        console.error('Login-Fehler im Store:', error);
        throw error;
      }
    },
    
    async logout() {
      // Verhindere parallele Logout-Aufrufe mit der globalen Variable
      if (logoutInProgress) {
        console.log('Logout wird bereits ausgeführt...');
        return false;
      }
      
      logoutInProgress = true;
      
      try {
        // CSRF-Token vor dem Logout abrufen (nur wenn nicht bereits vorhanden)
        if (!document.cookie.includes('XSRF-TOKEN')) {
          await fetchCSRFToken();
        }
        
        // API-Aufruf zum Logout mit Credentials
        await http.post(API_PATHS.LOGOUT, {}, {
          withCredentials: true
        });
        
        // Speicher bereinigen
        this.token = null;
        this.user = null;
        this.impersonating = false;
        this.originalUserId = null;
        
        // Alle relevanten Daten aus dem localStorage entfernen
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('impersonating');
        localStorage.removeItem('originalUserId');
        localStorage.removeItem('currentCompany');
        localStorage.removeItem('companyId');
        
        console.log('Logout durchgeführt, alle Daten wurden aus dem Store und localStorage entfernt');
        
        // Cookies löschen
        document.cookie.split(';').forEach(cookie => {
          const [name] = cookie.trim().split('=');
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        });
        
        return true;
      } catch (error) {
        console.error('Fehler beim Logout:', error);
        
        // Trotz API-Fehler lokales Logout durchführen
        this.token = null;
        this.user = null;
        this.impersonating = false;
        this.originalUserId = null;
        
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('impersonating');
        localStorage.removeItem('originalUserId');
        localStorage.removeItem('currentCompany');
        localStorage.removeItem('companyId');
        
        return false;
      } finally {
        logoutInProgress = false;
      }
    },
    
    async fetchCurrentUser() {
      try {
        const response = await http.get(API_PATHS.USER);
        this.setUser(response.data);
        return response.data;
      } catch (error) {
        console.error('Benutzerabruf-Fehler:', error);
        throw error;
      }
    },
    
    // Token bei Anwendungsstart wiederherstellen
    restoreToken() {
      if (this.token) {
        http.defaults.headers.common['Authorization'] = `Bearer ${this.token}`;
      }
    },
    
    // Impersonation eines anderen Benutzers (nur für Super-Admins)
    async impersonateUser(companyId) {
      try {
        // CSRF-Token vor der Anfrage abrufen
        await fetchCSRFToken();
        
        console.log('Impersoniere Firma mit ID:', companyId);
        
        const { data } = await http.post(`/api/admin/impersonate/company/${companyId}`, {}, {
          withCredentials: true,
          headers: {
            'X-XSRF-TOKEN': decodeURIComponent(getCookie('XSRF-TOKEN'))
          }
        });
        
        if (data.token) {
          // Token und Benutzerdaten im Store speichern
          this.setToken(data.token);
          this.setUser(data.user);
          this.setImpersonating(true);
          this.setOriginalUserId(data.original_user_id);
          
          // Firmendaten speichern
          if (data.company) {
            this.setCompany(data.company);
            console.log('Firmendaten beim Impersonieren gespeichert:', data.company);
          }
          
          // Lokalen Speicher aktualisieren
          localStorage.setItem('token', data.token);
          if (data.user) {
            localStorage.setItem('user', JSON.stringify(data.user));
          }
          
          // Event auslösen, dass die Auth geändert wurde
          // Andere Stores können darauf hören und reagieren
          const event = new CustomEvent(EventTypes.AUTH_IMPERSONATED, { 
            detail: { companyId, userId: data.user?.id, company: data.company } 
          });
          eventBus.dispatchEvent(event);
          
          return data;
        }
        
        throw new Error('Token nicht in der Antwort enthalten');
      } catch (error) {
        console.error('Fehler bei der Impersonation:', error.response ? `${error.response.status}: ${JSON.stringify(error.response.data)}` : error.message);
        throw error;
      }
    },
    
    // Beenden der Impersonation und Rückkehr zum ursprünglichen Benutzer
    async stopImpersonating() {
      try {
        console.log('Beende Impersonation...');
        
        // CSRF-Token vor der Anfrage abrufen
        await fetchCSRFToken();
        
        // Explizit die original_user_id im Request-Body senden
        const response = await http.post(API_PATHS.STOP_IMPERSONATING, {
          original_user_id: this.originalUserId || localStorage.getItem('originalUserId')
        }, {
          withCredentials: true,
          headers: {
            'X-XSRF-TOKEN': decodeURIComponent(getCookie('XSRF-TOKEN'))
          }
        });
        
        if (response.data.token) {
          // Token und Benutzerdaten aktualisieren
          this.setToken(response.data.token);
          this.setUser(response.data.user);
          this.setImpersonating(false);
          this.setOriginalUserId(null);
          
          // Firmendaten aktualisieren
          if (response.data.company) {
            this.setCompany(response.data.company);
          }
          
          // Event auslösen
          const event = new CustomEvent(EventTypes.AUTH_CHANGED, { 
            detail: { user: response.data.user, company: response.data.company } 
          });
          eventBus.dispatchEvent(event);
          
          // Zur Admin-Dashboard-Seite weiterleiten
          router.push('/admin/dashboard');
          
          return response.data;
        }
        
        throw new Error('Token nicht in der Antwort enthalten');
      } catch (error) {
        console.error('Fehler beim Beenden der Impersonation:', error.response ? `${error.response.status}: ${JSON.stringify(error.response.data)}` : error.message);
        
        // Bei einem 404-Fehler trotzdem lokale Daten zurücksetzen
        if (error.response && error.response.status === 404) {
          this.setImpersonating(false);
          this.setOriginalUserId(null);
          
          // Zum Login weiterleiten
          router.push('/login');
        }
        
        throw error;
      }
    },
    
    async initializeAuth() {
      try {
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');
        
        if (token && user) {
          this.setToken(token);
          this.setUser(JSON.parse(user));
          
          // Impersonation-Status wiederherstellen
          const impersonating = localStorage.getItem('impersonating');
          const originalUserId = localStorage.getItem('originalUserId');
          
          if (impersonating === 'true' && originalUserId) {
            this.setImpersonating(true);
            this.setOriginalUserId(originalUserId);
          }
          
          return true;
        }
        
        return false;
      } catch (error) {
        console.error('Fehler bei der Auth-Initialisierung:', error);
        // Bei Fehler, Daten zurücksetzen
        this.token = null;
        this.user = null;
        this.impersonating = false;
        this.originalUserId = null;
        return false;
      }
    },
    
    async getCsrfToken() {
      try {
        await fetchCSRFToken();
        return getCookie('XSRF-TOKEN');
      } catch (error) {
        console.error('Fehler beim Abrufen des CSRF-Tokens:', error);
        return null;
      }
    }
  },
  
  persist: {
    key: 'auth',
    storage: localStorage,
    paths: ['token', 'user', 'impersonating', 'originalUserId']
  }
}); 